import { loadGoogleMaps } from '@/utils/loadGoogleMaps';
import { searchPoisForText } from './poisApi';
import type { SearchPoiResult } from './poisApi';
import { searchDmasForText } from './dmaApi';
import type { SearchDmaResult } from './dmaApi';

export type { SearchPoiResult, SearchDmaResult };

export interface SearchSuggestion {
    id: string;
    label: string;
    /**
     * Discriminant: 'poi' for POI results, 'leak' for Find & Fix leak results (child rows —
     * no surface calls a leak a Point of Interest), 'dma' for DMA results, 'address' for
     * geocoder results.
     */
    source?: 'poi' | 'leak' | 'dma' | 'address';
    /** Raw POI/leak data — present when source is 'poi' or 'leak'. */
    poiData?: SearchPoiResult;
    /** Raw DMA data — present only when source === 'dma'. */
    dmaData?: SearchDmaResult;
    /** Present on address suggestions — enables session-aware detail fetching via toPlace() */
    placePrediction?: google.maps.places.PlacePrediction;
}

export interface SearchResult {
    id: string;
    label: string;
    subtitle?: string;
    source: 'geocoder' | 'poi';
    provider: 'google' | 'orbit';
    coordinates: [lng: number, lat: number];
    bbox?: [minLng: number, minLat: number, maxLng: number, maxLat: number];
}

export interface GooglePlacesServices {
    newSessionToken: () => google.maps.places.AutocompleteSessionToken;
}

export async function initGooglePlaces(apiKey: string): Promise<GooglePlacesServices | null> {
    if (!apiKey) return null;

    try {
        const g = await loadGoogleMaps(apiKey);
        const { AutocompleteSessionToken } = await g.maps.importLibrary('places');
        return { newSessionToken: () => new AutocompleteSessionToken() };
    } catch (err) {
        // Address search degrades silently to POI/DMA-only; surface a breadcrumb so an
        // init failure (e.g. the API loading) is diagnosable rather than invisible.
        console.warn('Google Places init failed; address search unavailable.', err);
        return null;
    }
}

export interface AutocompleteOptions {
    /**
     * Preference, not a restriction: suggestions near the bias rank first, but places
     * anywhere in the world stay reachable (a deliberate search for somewhere off-screen
     * must still work). Omitted when neither map bounds nor a client focal point exist,
     * in which case Google falls back to its own (IP-based) inference.
     */
    locationBias?: google.maps.places.LocationBias;
    /** Preferred language for suggestion text (e.g. the client's configured language). */
    language?: string;
}

export async function getSuggestions(
    input: string,
    sessionToken: google.maps.places.AutocompleteSessionToken,
    options?: AutocompleteOptions,
): Promise<SearchSuggestion[]> {
    try {
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken,
            includedPrimaryTypes: ['geocode'],
            ...(options?.locationBias ? { locationBias: options.locationBias } : {}),
            ...(options?.language ? { language: options.language } : {}),
        });
        return suggestions.flatMap((s) => {
            const pred = s.placePrediction;
            if (!pred) return [];
            return [{ id: pred.placeId, label: pred.text.toString(), placePrediction: pred }];
        });
    } catch {
        return [];
    }
}

export async function getPlaceDetails(
    placeId: string,
    placePrediction?: google.maps.places.PlacePrediction,
): Promise<SearchResult | null> {
    try {
        const place = placePrediction ? placePrediction.toPlace() : new google.maps.places.Place({ id: placeId });

        await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'viewport'] });

        const location = place.location;
        if (!location) return null;

        const lng = location.lng();
        const lat = location.lat();

        if (!isFinite(lng) || !isFinite(lat)) return null;

        const viewport = place.viewport;
        let bbox: SearchResult['bbox'];
        if (viewport) {
            const sw = viewport.getSouthWest();
            const ne = viewport.getNorthEast();
            bbox = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];
        }

        return {
            id: placeId,
            label: place.formattedAddress ?? place.displayName ?? '',
            source: 'geocoder',
            provider: 'google',
            coordinates: [lng, lat],
            bbox,
        };
    } catch {
        return null;
    }
}

/**
 * Searches DMAs by name or ident and maps each result to a SearchSuggestion with source='dma'.
 */
export async function getDmaSuggestions(term: string, clientId: string): Promise<SearchSuggestion[]> {
    try {
        const results = await searchDmasForText(term, clientId);
        return results.map((dma) => ({
            id: dma.ident,
            label: dma.name,
            source: 'dma' as const,
            dmaData: dma,
        }));
    } catch {
        return [];
    }
}

/** Matches "Stop 5", "stop 12", "STOP 3" etc. and captures the number. */
const STOP_KEYWORD_PATTERN = /^stop\s+(\d+)$/i;

/**
 * Searches POIs by text and maps each result to a SearchSuggestion. Root rows become
 * source='poi' ("Stop 5 — Manchester West"); child rows are recorded leaks and become
 * source='leak', labelled by their family suffix ("Stop 21-1 — Manchester West") so the
 * dropdown never presents a leak as a Point of Interest.
 *
 * When the term matches "Stop N" (case-insensitive), passes stopNumber to the backend
 * for an exact match on p.stop rather than a substring search.
 */
export async function getPoisSuggestions(term: string, clientId: string): Promise<SearchSuggestion[]> {
    try {
        const stopMatch = STOP_KEYWORD_PATTERN.exec(term);
        const stopNumber = stopMatch ? parseInt(stopMatch[1], 10) : undefined;
        const results = await searchPoisForText(term, clientId, stopNumber);
        return results.map((poi) => {
            const isLeak = poi.entityType === 'leak';
            const stopLabel = isLeak && poi.suffixLabel ? poi.suffixLabel : String(poi.stop);
            return {
                // Suggestion ids key the dropdown list — the entity id keeps a root and its
                // child leaks distinct even though they share family context.
                id: poi.entityId ?? poi.id,
                label: `Stop ${stopLabel} — ${poi.name}`,
                source: isLeak ? ('leak' as const) : ('poi' as const),
                poiData: poi,
            };
        });
    } catch {
        return [];
    }
}
